import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { getOrCreateClientId } from "../api/client-id";
import {
  listAllFeedback,
  listMyFeedback,
  type AllFeedbackItem,
  type AllFeedbackPage,
  type FeedbackListItem,
  type WidgetApiConfig,
} from "../api/client";
import { cacheGet, cacheSet } from "../api/cache";
import { activeTeamCode, readProfile } from "../api/profile";
import { useIsMobile } from "../hooks";
import { CloseIcon, ScreenshotIcon, VideoIcon } from "./icons";
import { FeedbackDetailView } from "./FeedbackDetailView";

type Props = {
  apiConfig: WidgetApiConfig;
  pillEdge: "left" | "right";
  pillTopPx: number;
  onClose: () => void;
};

type Phase = "idle" | "loading" | "ready" | "error";
type Tab = "all" | "mine";
type StatusFilter = "" | "new" | "open" | "in_progress" | "resolved" | "archived";

const STATUS_LABELS: Record<string, string> = {
  new: "New",
  // `open` sudah lama dikirim server tapi tak pernah punya labelnya sendiri di sini —
  // hasilnya lencana kosong pada laporan yang kartunya masih di To Do.
  open: "To do",
  in_progress: "In Progress",
  resolved: "Resolved",
  archived: "Archived",
};
const STATUS_TONES: Record<string, string> = {
  new: "mf-status-new",
  open: "mf-status-new",
  in_progress: "mf-status-progress",
  resolved: "mf-status-resolved",
  archived: "mf-status-closed",
};

function relTime(at: number | string): string {
  const now = Date.now();
  const diff = Math.max(0, now - new Date(at).getTime());
  const m = Math.round(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d ago`;
  return `${Math.round(d / 30)}mo ago`;
}

function shortUrl(u: string): string {
  try {
    const p = new URL(u);
    const path = p.pathname.length > 30 ? `${p.pathname.slice(0, 27)}…` : p.pathname;
    return `${p.host}${path}`;
  } catch {
    return u.length > 50 ? `${u.slice(0, 47)}…` : u;
  }
}

function useDebounce<T>(value: T, ms: number): T {
  const [d, setD] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setD(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return d;
}

// Cache keys
function allKey(projectKey: string, status: string, internal: boolean) {
  // `internal` is part of the key so switching QA role never serves a cached
  // list that includes (or omits) internal items for the wrong viewer.
  return `all:${projectKey}:${status}:${internal ? "int" : "cli"}`;
}
function mineKey(projectKey: string, clientId: string) {
  return `mine:${projectKey}:${clientId}`;
}

export function ViewAllMode({ apiConfig, pillEdge, pillTopPx, onClose }: Props) {
  const [tab, setTab] = useState<Tab>("mine");
  const [selected, setSelected] = useState<FeedbackListItem | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  // ── My Feedback ──
  const [myItems, setMyItems] = useState<FeedbackListItem[]>([]);
  const [myPhase, setMyPhase] = useState<Phase>("idle");
  const [myStatus, setMyStatus] = useState<StatusFilter>("");

  // ── All Feedback ──
  const [allItems, setAllItems] = useState<AllFeedbackItem[]>([]);
  const [allPhase, setAllPhase] = useState<Phase>("idle");
  const [allStatus, setAllStatus] = useState<StatusFilter>("");
  const [allHasMore, setAllHasMore] = useState(false);
  const [allNextOffset, setAllNextOffset] = useState<number | null>(null);
  const [allLoadingMore, setAllLoadingMore] = useState(false);
  const [allTotal, setAllTotal] = useState(0);
  const [allFetchedOnce, setAllFetchedOnce] = useState(false);

  const [rawSearch, setRawSearch] = useState("");
  const search = useDebounce(rawSearch, 200);
  useEffect(() => { setRawSearch(""); }, [tab]);

  // ── Fetch "My feedback" (default tab) on mount ──
  // SWR pattern: serve stale immediately → show spinner only on first load.
  useEffect(() => {
    const clientId = getOrCreateClientId();
    const key = mineKey(apiConfig.projectKey, clientId);
    const stale = cacheGet<FeedbackListItem[]>(key);
    if (stale) {
      setMyItems(stale);
      setMyPhase("ready");
    } else {
      setMyPhase("loading");
    }
    let cancelled = false;
    listMyFeedback(apiConfig, clientId)
      .then((items) => {
        if (cancelled) return;
        cacheSet(key, items);
        setMyItems(items);
        setMyPhase("ready");
      })
      .catch(() => {
        if (cancelled) return;
        if (!stale) setMyPhase("error");
      });
    return () => { cancelled = true; };
  }, [apiConfig]);

  // ── Fetch "All feedback" lazily (only when tab first becomes active) ──
  useEffect(() => {
    if (tab !== "all") return;
    const teamCode = activeTeamCode(readProfile());
    // Kunci cache ikut menandai "boleh lihat semua", supaya daftar yang terisi
    // karena kode tidak tersaji lagi sesudah kodenya dicabut, dan sebaliknya.
    const key = allKey(apiConfig.projectKey, allStatus, Boolean(teamCode) || Boolean(apiConfig.publicList));
    const stale = cacheGet<AllFeedbackPage>(key);
    if (stale) {
      setAllItems(stale.feedback);
      setAllHasMore(stale.hasMore);
      setAllNextOffset(stale.nextOffset);
      setAllTotal(stale.totalCount);
      setAllPhase("ready");
    } else if (!allFetchedOnce) {
      setAllPhase("loading");
    }
    let cancelled = false;
    listAllFeedback(apiConfig, { status: allStatus || undefined, teamCode })
      .then((page) => {
        if (cancelled) return;
        cacheSet(key, page);
        setAllItems(page.feedback);
        setAllHasMore(page.hasMore);
        setAllNextOffset(page.nextOffset);
        setAllTotal(page.totalCount);
        setAllPhase("ready");
        setAllFetchedOnce(true);
      })
      .catch(() => {
        if (cancelled) return;
        if (!stale) setAllPhase("error");
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, apiConfig, allStatus]);

  function loadMore() {
    if (!allNextOffset || allLoadingMore) return;
    setAllLoadingMore(true);
    const teamCode = activeTeamCode(readProfile());
    listAllFeedback(apiConfig, { status: allStatus || undefined, offset: allNextOffset, teamCode })
      .then((page) => {
        setAllItems((prev) => [...prev, ...page.feedback]);
        setAllHasMore(page.hasMore);
        setAllNextOffset(page.nextOffset);
      })
      .catch(() => {})
      .finally(() => setAllLoadingMore(false));
  }

  // Filtered views (client-side search)
  const filteredAll = useMemo(() => {
    if (!search) return allItems;
    const q = search.toLowerCase();
    return allItems.filter(
      (it) =>
        it.title.toLowerCase().includes(q) ||
        (it.url ?? "").toLowerCase().includes(q) ||
        (it.reporter?.name ?? "").toLowerCase().includes(q) ||
        (it.reporter?.email ?? "").toLowerCase().includes(q),
    );
  }, [allItems, search]);

  const filteredMine = useMemo(() => {
    let items = myStatus ? myItems.filter((it) => it.status === myStatus) : myItems;
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter(
      (it) => it.title.toLowerCase().includes(q) || (it.url ?? "").toLowerCase().includes(q),
    );
  }, [myItems, myStatus, search]);

  // Outside-click to close
  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === "Escape") onClose(); }
    function onPointerDown(e: PointerEvent) {
      // When detail view is active the popover is unmounted — don't close.
      if (!popoverRef.current) return;
      const path = e.composedPath?.() ?? [];
      const inside = path.some(
        (n) => n === popoverRef.current || (n instanceof Node && popoverRef.current!.contains(n)),
      );
      if (inside) return;
      for (const n of path) {
        if (n instanceof HTMLElement && n.classList?.contains("mf-pill-btn")) return;
      }
      onClose();
    }
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [onClose]);

  if (selected) {
    return <FeedbackDetailView item={selected} apiConfig={apiConfig} onBack={() => setSelected(null)} />;
  }

  const popoverStyle: Record<string, string> =
    pillTopPx < 0
      ? { top: "50%", transform: "translateY(-50%)" }
      : { top: `${Math.max(16, pillTopPx)}px` };
  if (pillEdge === "right") popoverStyle.right = "78px";
  else popoverStyle.left = "78px";

  const activeStatusFilter = tab === "all" ? allStatus : myStatus;
  const setStatusFilter = (s: StatusFilter) =>
    tab === "all" ? setAllStatus(s) : setMyStatus(s);
  const currentPhase = tab === "all" ? allPhase : myPhase;
  const isStale = tab === "all"
    ? allPhase === "loading" && allItems.length > 0
    : myPhase === "loading" && myItems.length > 0;

  // Sama persis dengan yang bisa dipulangkan server (statusOf di feedback.ts).
  // "Closed" warisan brightflow dibuang: ia tidak pernah ada di sisi maua, jadi
  // memilihnya selalu berarti daftar kosong tanpa sebab yang terlihat.
  const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
    { value: "", label: "All" },
    { value: "new", label: "New" },
    { value: "open", label: "To do" },
    { value: "in_progress", label: "In Progress" },
    { value: "resolved", label: "Resolved" },
    { value: "archived", label: "Archived" },
  ];

  return (
    <>
      {/* Dim backdrop only in drawer (mobile) mode — desktop keeps the page visible. */}
      {isMobile && <div class="mf-list-backdrop" onClick={onClose} aria-hidden="true" />}
      <div
        ref={popoverRef}
        class={`mf-list-popover ${isMobile ? "is-drawer" : ""}`}
        role="dialog"
        aria-label="Feedback"
        // On mobile the drawer position is fully driven by CSS; inline styles
        // would otherwise win over the media query.
        style={isMobile ? undefined : popoverStyle}
      >
      {/* Header */}
      <div class="mf-list-header">
        <div class="mf-list-header-left">
          <h2 class="mf-list-title">Feedback</h2>
          {isStale && <span class="mf-list-refreshing" title="Refreshing…">↻</span>}
        </div>
        <button type="button" class="mf-tool" onClick={onClose} aria-label="Close">
          <CloseIcon />
        </button>
      </div>

      {/* Tabs */}
      <div class="mf-list-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "mine"}
          class={`mf-list-tab ${tab === "mine" ? "is-active" : ""}`}
          onClick={() => setTab("mine")}
        >
          My feedback
          {myItems.length > 0 && <span class="mf-list-tab-count">{myItems.length}</span>}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "all"}
          class={`mf-list-tab ${tab === "all" ? "is-active" : ""}`}
          onClick={() => setTab("all")}
        >
          All feedback
          {allTotal > 0 && <span class="mf-list-tab-count">{allTotal}</span>}
        </button>
      </div>

      {/* Search + filter */}
      <div class="mf-list-toolbar">
        <div class="mf-list-search-wrap">
          <svg class="mf-list-search-icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="8.5" cy="8.5" r="5" />
            <line x1="13" y1="13" x2="17" y2="17" />
          </svg>
          <input
            type="search"
            class="mf-list-search"
            placeholder="Search…"
            value={rawSearch}
            onInput={(e) => setRawSearch((e.target as HTMLInputElement).value)}
            aria-label="Search feedback"
          />
          {rawSearch && (
            <button type="button" class="mf-list-search-clear" onClick={() => setRawSearch("")} aria-label="Clear">×</button>
          )}
        </div>
        <div class="mf-list-filters" role="group" aria-label="Filter by status">
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              class={`mf-list-filter-btn ${activeStatusFilter === opt.value ? "is-active" : ""}`}
              onClick={() => setStatusFilter(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      <div class="mf-list-body">
        {/* First-load spinner (only when no stale data) */}
        {currentPhase === "loading" && (tab === "all" ? allItems.length === 0 : myItems.length === 0) && (
          <div class="mf-list-state">
            <div class="mf-spinner" />
            <div>Loading…</div>
          </div>
        )}

        {currentPhase === "error" && (
          <div class="mf-list-state mf-list-state-error">Couldn&apos;t load feedback.</div>
        )}

        {/* ALL tab */}
        {tab === "all" && (allPhase === "ready" || allItems.length > 0) && (
          filteredAll.length === 0 ? (
            <div class="mf-list-state">
              <div style={{ fontSize: 12, color: "#6b7280" }}>
                {search || allStatus ? "No results." : "No feedback yet."}
              </div>
            </div>
          ) : (
            <>
              <ul class="mf-list">
                {filteredAll.map((it) => (
                  <li key={it.id}>
                    <button
                      type="button"
                      class="mf-list-item mf-list-item-btn"
                      onClick={() => setSelected(it)}
                      aria-label={`View: ${it.title}`}
                    >
                      <div class="mf-list-item-row">
                        <span class={`mf-status-pill ${STATUS_TONES[it.status] ?? ""}`}>{STATUS_LABELS[it.status] ?? it.status}</span>
                        <span class="mf-list-item-title">{it.title}</span>
                        {it.hasVideo && <span class="mf-list-attach" title="Has recording"><VideoIcon /></span>}
                        {it.hasScreenshot && <span class="mf-list-attach" title="Has screenshot"><ScreenshotIcon /></span>}
                      </div>
                      <div class="mf-list-item-meta">
                        {relTime(it.createdAt)}
                        {it.url ? <> · {shortUrl(it.url)}</> : null}
                        {it.reporter?.name || it.reporter?.email ? <> · {it.reporter.name ?? it.reporter.email}</> : null}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
              {allHasMore && !search && (
                <div class="mf-list-load-more">
                  <button type="button" class="mf-list-load-more-btn" onClick={loadMore} disabled={allLoadingMore}>
                    {allLoadingMore ? "Loading…" : `Load more`}
                  </button>
                </div>
              )}
            </>
          )
        )}

        {/* MINE tab */}
        {tab === "mine" && (myPhase === "ready" || myItems.length > 0) && (
          filteredMine.length === 0 ? (
            <div class="mf-list-state">
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                {search || myStatus ? "No results." : "No feedback from this browser"}
              </div>
              {!search && !myStatus && (
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  Submit feedback via screenshot or recording.
                </div>
              )}
            </div>
          ) : (
            <ul class="mf-list">
              {filteredMine.map((it) => (
                <li key={it.id}>
                  <button
                    type="button"
                    class="mf-list-item mf-list-item-btn"
                    onClick={() => setSelected(it)}
                    aria-label={`View: ${it.title}`}
                  >
                    <div class="mf-list-item-row">
                      <span class={`mf-status-pill ${STATUS_TONES[it.status] ?? ""}`}>{STATUS_LABELS[it.status] ?? it.status}</span>
                      <span class="mf-list-item-title">{it.title}</span>
                      {it.hasVideo && <span class="mf-list-attach"><VideoIcon /></span>}
                      {it.hasScreenshot && <span class="mf-list-attach"><ScreenshotIcon /></span>}
                    </div>
                    <div class="mf-list-item-meta">
                      {relTime(it.createdAt)}
                      {it.url ? <> · {shortUrl(it.url)}</> : null}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )
        )}
      </div>
      </div>
    </>
  );
}
